import Header from '../Components/Header';
import Footer from '../Components/Footer';
import DeliverableSection from '../Components/DeliverableSection';
import { Helmet } from 'react-helmet';
import { SITE_ORIGIN, canonicalUrl } from '../siteConfig';
import { SERVICE_AREAS, SCHEMA_AREA_SERVED } from '../serviceAreas';

const PAGE_URL = canonicalUrl('/services/domains-dns-integrations');

const SCHEMA_SUB_SERVICES = [
  {
    name: 'Domain and DNS Management',
    description: 'Registrar review, DNS record updates, and documentation for business domains.',
  },
  {
    name: 'Email Authentication',
    description: 'SPF, DKIM, and DMARC DNS records configured for Microsoft 365 and other mail platforms.',
  },
  {
    name: 'SaaS and Website Integrations',
    description: 'DNS and SSL setup connecting domains to hosting, Microsoft 365, and third-party services.',
  },
];

function buildStructuredData(faqs) {
  const townAreas = SERVICE_AREAS.map((name) => ({ '@type': 'City', name }));

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: `${SITE_ORIGIN}/`,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Services',
            item: `${SITE_ORIGIN}/services`,
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: 'Domains, DNS and Integrations',
            item: PAGE_URL,
          },
        ],
      },
      {
        '@type': 'Service',
        '@id': `${PAGE_URL}#domains-dns-integrations`,
        name: 'Domains, DNS and Integrations',
        description:
          'Domain registration support, DNS troubleshooting, email authentication, SSL setup, and SaaS integrations for businesses in Westchester and Fairfield County.',
        url: PAGE_URL,
        dateModified: '2026-07-04',
        provider: {
          '@type': 'LocalBusiness',
          '@id': `${SITE_ORIGIN}/#localbusiness`,
          name: 'Solace Integrations',
          url: SITE_ORIGIN,
          telephone: '+19145641865',
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Hawthorne',
            addressRegion: 'NY',
            addressCountry: 'US',
          },
          areaServed: SCHEMA_AREA_SERVED,
        },
        areaServed: [
          { '@type': 'AdministrativeArea', name: 'Westchester County, NY' },
          { '@type': 'AdministrativeArea', name: 'Fairfield County, CT' },
          ...townAreas,
        ],
        hasPart: SCHEMA_SUB_SERVICES.map((service) => ({
          '@type': 'Service',
          name: service.name,
          description: service.description,
          provider: { '@id': `${SITE_ORIGIN}/#localbusiness` },
          areaServed: townAreas,
        })),
      },
      {
        '@type': 'FAQPage',
        '@id': `${PAGE_URL}#faq`,
        mainEntity: faqs.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer,
          },
        })),
      },
    ],
  };
}

const DELIVERABLES = [
  {
    title: 'DNS Review',
    icon: 'fas fa-search',
    image: 'assets/images/dns.png',
    imageAlt: 'Placeholder illustration of DNS record review',
    lead: 'Know what your domain is pointing to today.',
    body: 'We pull together the current DNS records for your domain, note what each one does, and flag anything that looks outdated or conflicting. That gives us a starting point before we change hosting, email, or add a new service. When changes are ready, we document old and new values so you can roll back if something does not propagate as expected.',
    points: [
      'Current A, CNAME, MX, and TXT records documented',
      'Which services each record supports (website, email, SaaS tools)',
      'Conflicts or duplicates that could cause problems',
      'Recommended changes before a migration or cutover',
      'Verification that the website, email, or service resolves after updates',
    ],
  },
];

const INTEGRATION_DEPLOYMENT = {
  title: 'SaaS and Service Setup',
  icon: 'fas fa-plug',
  image: 'assets/images/saas.png',
  imageAlt: 'Placeholder illustration of SaaS service integration through DNS',
  lead: 'Connect the tools your business already uses.',
  body: 'Many SaaS products require a DNS TXT or CNAME record to verify domain ownership, route traffic, or enable single sign-on. We add those records, confirm the vendor accepts them, and note what each integration needs to stay working.',
  points: [
    'Domain verification records for Microsoft 365 and other platforms',
    'Custom subdomain setup for tools that need it',
    'SSL certificate requests or renewals tied to your domain',
    'Handoff notes listing each integration and its DNS dependency',
  ],
};

const KEY_BENEFITS = [
  {
    title: 'Documented before changes',
    text: 'We capture what your DNS looks like today before touching anything. That makes troubleshooting and rollbacks much easier when you are moving hosting or email.',
  },
  {
    title: 'Email that actually sends',
    text: 'SPF, DKIM, and DMARC records help your outbound mail reach inboxes instead of spam folders. We configure them for your mail platform and check that they validate.',
  },
  {
    title: 'Fewer surprise breakages',
    text: 'A domain change can knock out email, your website, or a booking link if you miss a record. We map dependencies first so one update does not take down something else.',
  },
];

const DOMAINS_DNS_PRODUCTS = [
  {
    name: 'Domain Registration',
    summary:
      'Help registering new domains or transferring existing ones to a registrar you control, with renewal reminders documented.',
    features: [
      'New domain registration through common registrars',
      'Transfer planning from an old registrar or web designer account',
      'Renewal dates and billing contacts documented in your handoff',
      'Nameserver updates when DNS moves to a new host',
    ],
  },
  {
    name: 'DNS Management',
    summary:
      'Day-to-day DNS record updates at your registrar or DNS host. When you move hosting, switch email providers, or point a subdomain elsewhere, we update records carefully and walk through verification with you.',
    features: [
      'A, CNAME, MX, and TXT record updates at your DNS host or registrar',
      'Subdomain setup for tools, staging sites, or internal apps',
      'TTL reduction before major changes when the provider allows it',
      'Record documentation and rollback notes after every change',
    ],
  },
  {
    name: 'Website Hosting and Migrations',
    summary:
      'DNS cutovers when you move a website to new hosting. We coordinate with your web host or developer, update records at the switch, and confirm the site loads over HTTPS before calling it done.',
    features: [
      'DNS cutover planning with your web host or developer',
      'A and CNAME record updates for the new hosting environment',
      'Verification that the site and email resolve correctly after the change',
      'SSL certificate check once the domain points to the new server',
    ],
  },
];

const EMAIL_AUTH_PRODUCTS = [
  {
    name: 'SPF Records',
    summary:
      'SPF tells receiving mail servers which hosts are allowed to send email for your domain. We add or update the TXT record to match your mail platform.',
    features: [
      'SPF TXT record aligned with Microsoft 365 or your mail provider',
      'Review of existing includes so records do not exceed lookup limits',
      'Documentation of which services are authorized to send',
    ],
    vendorHref: 'https://learn.microsoft.com/en-us/defender-office-365/email-authentication-spf-configure',
    vendorLabel: 'Microsoft SPF guidance',
  },
  {
    name: 'DKIM Signing',
    summary:
      'DKIM adds a cryptographic signature to outbound mail. We enable signing in your mail platform and publish the public key as a DNS CNAME or TXT record.',
    features: [
      'DKIM selector setup in Microsoft 365 or your mail provider',
      'DNS records published and verified by the platform',
      'Confirmation that outbound messages pass DKIM checks',
    ],
    vendorHref: 'https://learn.microsoft.com/en-us/defender-office-365/email-authentication-dkim-configure',
    vendorLabel: 'Microsoft DKIM guidance',
  },
  {
    name: 'DMARC Policy',
    summary:
      'DMARC tells receiving servers what to do when SPF or DKIM fails, and can send you reports about mail using your domain.',
    features: [
      'DMARC TXT record starting at a monitoring policy (p=none) when appropriate',
      'Alignment checks so SPF and DKIM work together',
      'Guidance on tightening policy once mail flows are confirmed',
    ],
    vendorHref: 'https://learn.microsoft.com/en-us/defender-office-365/email-authentication-dmarc-configure',
    vendorLabel: 'Microsoft DMARC guidance',
  },
];

const INTEGRATION_PRODUCTS = [
  {
    name: 'Microsoft 365 Domain Setup',
    summary:
      'Connect your business domain to Microsoft 365 for email, Teams, and SharePoint. We add the verification and service records Microsoft requires.',
    features: [
      'Domain verification TXT record',
      'MX and autodiscover records for email routing',
      'SPF, DKIM, and DMARC configured alongside mail setup',
    ],
  },
  {
    name: 'SSL Certificates',
    summary:
      'SSL certificates encrypt traffic between visitors and your website. Most hosts issue them automatically once DNS points correctly; we verify coverage and flag gaps.',
    features: [
      'Certificate status check after DNS changes',
      'Mixed-content or expired certificate troubleshooting',
      'Coordination with your web host when manual steps are needed',
    ],
  },
  {
    name: 'Third-Party SaaS Tools',
    summary:
      'CRM, booking, marketing, and other SaaS products often need a DNS record to verify your domain or route a subdomain.',
    features: [
      'TXT or CNAME records for domain verification',
      'Subdomain routing for customer portals or branded links',
      'Documentation of which vendor owns each record',
    ],
  },
];

const WHY_DNS = [
  'Most small offices have DNS spread across a registrar, a web host, and one or more SaaS tools with no single map of what depends on what.',
  'A misconfigured MX or SPF record can stop email from sending or land it in spam without an obvious error in Outlook.',
  'Documenting records before a change makes rollbacks straightforward when a cutover does not go as planned.',
  'Hosting moves, email platform switches, and subdomain changes all follow the same pattern: update records, verify resolution, document what changed.',
  'We work with the DNS host and registrar you already use rather than forcing a specific platform.',
];

const WHY_EMAIL_AUTH = [
  'SPF, DKIM, and DMARC are standard requirements for reliable outbound email today.',
  'Microsoft 365 includes the tools to sign mail; the DNS side still needs to be published correctly.',
  'Starting with a monitoring DMARC policy lets you see problems before enforcing stricter rules.',
  'Authentication records need to stay aligned when you add a new sending service like a newsletter tool.',
];

const FAQS = [
  {
    question: 'Do you sell domain names?',
    answer:
      'We help you register or transfer domains through common registrars like Cloudflare, GoDaddy, or Namecheap. The domain stays in your account; we document access and handle the DNS work.',
  },
  {
    question: 'Can you fix email going to spam?',
    answer:
      'Often the issue is missing or incorrect SPF, DKIM, or DMARC records. We review your DNS, align records with your mail platform, and test that authentication passes. Other factors like list quality and content still affect delivery, but authentication is the baseline.',
  },
  {
    question: 'We are moving our website to new hosting. Can you handle the DNS?',
    answer:
      'Yes. We coordinate with your web host or developer on the records needed, update DNS at cutover, verify the site loads over HTTPS, and document the old values in case you need to roll back.',
  },
  {
    question: 'Our last IT person set up DNS and nobody knows what the records do.',
    answer:
      'That is a common starting point. We audit the current records, document what each one points to, and flag anything that looks unused or conflicting before making changes.',
  },
  {
    question: 'Do you manage DNS ongoing?',
    answer:
      'We handle DNS as part of project work like migrations, new SaaS rollouts, or email platform changes. For day-to-day record tweaks outside a project, contact us and we can discuss what you need.',
  },
  {
    question: 'Can you set up a subdomain for a tool like our CRM or booking system?',
    answer:
      'Yes. Most SaaS tools provide a CNAME or TXT record for verification or routing. We add the record, confirm the vendor accepts it, and note it in your documentation.',
  },
  {
    question: 'What is DMARC and do we need it?',
    answer:
      'DMARC tells receiving mail servers how to handle messages that fail SPF or DKIM checks. It also provides reporting on mail sent using your domain. We typically start with a monitoring policy so you can see activity before enforcing stricter rules.',
  },
  {
    question: 'Can you connect our domain to Microsoft 365?',
    answer:
      'Yes. We add the verification record, configure MX and autodiscover records for email, and set up SPF, DKIM, and DMARC as part of the same project.',
  },
];

function FaqItem({ question, answer }) {
  return (
    <article className="unifi-page-faq-item">
      <h3 className="unifi-page-faq-question">{question}</h3>
      <p className="unifi-page-faq-answer">{answer}</p>
    </article>
  );
}

function ProductCard({ name, summary, features, vendorHref, vendorLabel }) {
  return (
    <article className="unifi-page-product-card">
      <h3 className="unifi-page-product-name">{name}</h3>
      <p className="unifi-page-product-summary">{summary}</p>
      <ul className="unifi-page-product-features">
        {features.map((feature) => (
          <li key={feature}>{feature}</li>
        ))}
      </ul>
      {vendorHref && vendorLabel && (
        <a
          href={vendorHref}
          target="_blank"
          rel="noopener noreferrer"
          className="unifi-page-product-link"
        >
          {vendorLabel}
        </a>
      )}
    </article>
  );
}

export default function DomainsDnsIntegrations() {
  const structuredData = buildStructuredData(FAQS);

  return (
    <div>
      <Helmet>
        <title>Domains, DNS &amp; Integrations in Westchester &amp; Fairfield County | Solace Integrations</title>
        <link rel="canonical" href={PAGE_URL} />
        <meta
          name="description"
          content="Domain management, DNS troubleshooting, SPF DKIM DMARC setup, SSL verification, and SaaS integrations for businesses in Westchester and Fairfield County."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={PAGE_URL} />
        <meta
          property="og:title"
          content="Domains, DNS &amp; Integrations in Westchester &amp; Fairfield County | Solace Integrations"
        />
        <meta
          property="og:description"
          content="DNS management, email authentication, domain migrations, and SaaS integrations for offices in Westchester and Fairfield County."
        />
        <meta property="og:image" content={`${SITE_ORIGIN}/assets/images/dns-records-placeholder.svg`} />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>
      <Header isFixed />
      <section className="services">
        <div className="services-hero py-5">
          <div className="container py-md-5 py-2">
            <nav className="net-infra-breadcrumb" aria-label="Breadcrumb">
              <a href="/services">Services</a>
              <span className="net-infra-breadcrumb-sep" aria-hidden="true">/</span>
              <span>Domains, DNS &amp; Integrations</span>
            </nav>
            <div className="text-center">
              <p className="unifi-page-hero-label">Domains, DNS &amp; Integrations</p>
              <h1 className="services-title text-center mb-lg-4 mb-3 px-lg-5">
                Domains, DNS &amp; Integrations in Westchester &amp; Fairfield County
              </h1>
              <p className="main_p4 mt-4 mb-0 pt-2 text-center mx-auto text-white">
                Domain registration, DNS troubleshooting, email authentication, SSL verification,
                and the DNS records that connect your website and SaaS tools. Documented before
                anything changes.
              </p>
              <div className="unifi-page-hero-actions">
                <a href="/contact" className="btn btn-style solace-btn-primary">
                  Plan Your DNS Project
                </a>
                <a href="/contact" className="btn btn-style unifi-page-hero-secondary">
                  Talk With Us
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="overview" className="unifi-page-section">
        <div className="container">
          <div className="unifi-page-overview-grid">
            <div className="unifi-page-overview-main">
              <h6 className="solace-title-subhny mb-2">Overview</h6>
              <h2 className="title-w3l text-left">
                Your domain connects more than a website
              </h2>
              <p className="unifi-page-body">
                Solace Integrations helps businesses in Westchester and Fairfield County manage
                the DNS layer behind their domain: where email routes, how SSL certificates
                validate, and which SaaS tools verify ownership through TXT or CNAME records.
              </p>
              <p className="unifi-page-body">
                We start by documenting what your DNS looks like today, then make changes with
                a clear rollback plan. Whether you are moving hosting, connecting Microsoft 365,
                or fixing mail that lands in spam, the work is scoped to your registrar and DNS
                host rather than a one-size-fits-all platform.
              </p>
              <div className="unifi-page-benefits">
                {KEY_BENEFITS.map(({ title, text }) => (
                  <article key={title} className="unifi-page-benefit-card">
                    <h3 className="unifi-page-benefit-title">{title}</h3>
                    <p className="unifi-page-benefit-text">{text}</p>
                  </article>
                ))}
              </div>
            </div>
            <aside className="unifi-page-snapshot">
              <h3 className="unifi-page-snapshot-title">At a Glance</h3>
              <dl className="unifi-page-snapshot-list">
                <div>
                  <dt>Domains</dt>
                  <dd>Registration, transfer, renewal tracking</dd>
                </div>
                <div>
                  <dt>Email auth</dt>
                  <dd>SPF, DKIM, DMARC</dd>
                </div>
                <div>
                  <dt>Coverage area</dt>
                  <dd>Westchester &amp; Fairfield County</dd>
                </div>
              </dl>
              <h4 className="unifi-page-snapshot-related">Related services</h4>
              <ul className="unifi-page-snapshot-links">
                <li>
                  <a href="/services">All Services</a>
                </li>
                <li>
                  <a href="/services/network-infrastructure">Network Infrastructure</a>
                </li>
                <li>
                  <a href="/services/storage-and-backup">Storage &amp; Backup</a>
                </li>
              </ul>
            </aside>
          </div>
        </div>
      </section>

      <section id="deliverables" className="net-infra-deliverables">
        <div className="container">
          <div className="text-center mb-5">
            <h6 className="solace-title-subhny mb-2">Project Deliverables</h6>
            <h2 className="title-w3l">What you get along the way</h2>
            <p className="net-infra-section-intro mx-auto">
              Before any DNS record changes, we document what exists today.
            </p>
          </div>
          {DELIVERABLES.map((deliverable) => (
            <DeliverableSection key={deliverable.title} {...deliverable} />
          ))}
        </div>
      </section>

      <section id="domains-dns" className="unifi-page-section unifi-page-section-alt">
        <div className="container">
          <div className="text-center mb-5">
            <h6 className="solace-title-subhny mb-2">Domains &amp; DNS</h6>
            <h2 className="title-w3l">Registration, records, and hosting cutovers</h2>
            <p className="net-infra-section-intro mx-auto">
              Domain work covers registration and renewal tracking, day-to-day record updates, and
              cutovers when you move hosting or switch email providers. Changes are verified and
              documented before anything goes live.
            </p>
          </div>
          <div className="unifi-page-product-grid unifi-page-product-grid-wide">
            {DOMAINS_DNS_PRODUCTS.map((product) => (
              <ProductCard key={product.name} {...product} />
            ))}
          </div>
          <ul className="unifi-page-why-list mt-5">
            {WHY_DNS.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      </section>

      <section id="email-auth" className="unifi-page-section">
        <div className="container">
          <div className="text-center mb-5">
            <h6 className="solace-title-subhny mb-2">Email Authentication</h6>
            <h2 className="title-w3l">SPF, DKIM, and DMARC</h2>
            <p className="net-infra-section-intro mx-auto">
              Outbound email needs DNS records that prove your messages are legitimate. We configure
              them for your mail platform and verify they pass authentication checks.
            </p>
          </div>
          <div className="unifi-page-product-grid unifi-page-product-grid-wide">
            {EMAIL_AUTH_PRODUCTS.map((product) => (
              <ProductCard key={product.name} {...product} />
            ))}
          </div>
          <ul className="unifi-page-why-list mt-5">
            {WHY_EMAIL_AUTH.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      </section>

      <section id="integrations" className="unifi-page-section unifi-page-section-alt">
        <div className="container">
          <div className="text-center mb-5">
            <h6 className="solace-title-subhny mb-2">Integrations</h6>
            <h2 className="title-w3l">Connecting domains to the tools you use</h2>
            <p className="net-infra-section-intro mx-auto">
              Microsoft 365, SSL certificates, CRM platforms, and other SaaS products all rely on
              DNS records. We add them, confirm they validate, and document what each one does.
            </p>
          </div>
          <div className="unifi-page-product-grid unifi-page-product-grid-wide">
            {INTEGRATION_PRODUCTS.map((product) => (
              <ProductCard key={product.name} {...product} />
            ))}
          </div>
        </div>
      </section>

      <section id="deployment" className="net-infra-install">
        <div className="container">
          <div className="text-center mb-5">
            <h6 className="solace-title-subhny mb-2">Deployment</h6>
            <h2 className="title-w3l">How we make the changes</h2>
            <p className="net-infra-section-intro mx-auto">
              SaaS integrations follow a documented process: records are added, verified with the
              vendor, and noted in your handoff materials.
            </p>
          </div>
          <DeliverableSection {...INTEGRATION_DEPLOYMENT} />
        </div>
      </section>

      <section id="coverage" className="unifi-page-section">
        <div className="container">
          <div className="text-center mb-5">
            <h6 className="solace-title-subhny mb-2">Service Areas</h6>
            <h2 className="title-w3l">Westchester &amp; Fairfield County coverage</h2>
            <p className="net-infra-section-intro mx-auto">
              We support domain and DNS projects for professional offices and small businesses
              across Westchester and Fairfield County, including White Plains, Scarsdale, Darien,
              Greenwich, and Stamford.
            </p>
          </div>
          <ul className="unifi-page-areas">
            {SERVICE_AREAS.map((area) => (
              <li key={area}>{area}</li>
            ))}
          </ul>
        </div>
      </section>

      <section id="faqs" className="unifi-page-section unifi-page-section-alt">
        <div className="container">
          <div className="text-center mb-5">
            <h6 className="solace-title-subhny mb-2">FAQs</h6>
            <h2 className="title-w3l">Questions about domains and DNS</h2>
          </div>
          <div className="unifi-page-faq-grid">
            {FAQS.map((faq) => (
              <FaqItem key={faq.question} {...faq} />
            ))}
          </div>
        </div>
      </section>

      <section className="net-infra-cta">
        <div className="container text-center">
          <h4 className="net-infra-cta-title">Ready to sort out your domain and DNS?</h4>
          <p className="net-infra-cta-text">
            Tell us what is not working or what you are planning to move. We will outline the
            records involved and what a cutover looks like.
          </p>
          <a href="/contact" className="btn btn-style solace-btn-primary">
            Start a Conversation
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
